import React, { useEffect, useState } from "react";
import { getProducts } from "./getProducts";
import { FaDollarSign, FaHeart } from "react-icons/fa";
import { Link } from "react-router-dom";
import addToCart from "../cart/addToCart";
import addToFavorites from "../favorites/addToFav";
import { useAuth } from "../../contexts/AuthContext";
import { User } from "firebase/auth";

type Product = {
  id: string;
  name: string;
  image: string;
  price: string;
  rating: number;
  reviews: number;
  link: string;
};

type ProductCardProps = Product & {
  onFavoriteToggle: (id: string) => void;
  isFavorite: boolean;
  currentUser: User | null;
};

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  price,
  image,
  rating,
  reviews,
  link,
  onFavoriteToggle,
  currentUser,
  isFavorite,
}) => {
  const handleAddToCart = async () => {
    if (!currentUser) {
      alert("Please log in to add items to your cart.");
      return;
    }

    try {
      await addToCart(currentUser.uid, id);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const handleToggleFavorite = async () => {
    if (!currentUser) {
      alert("Please log in to add items to your favorites.");
      return;
    }

    try {
      await addToFavorites(currentUser.uid, id);
      onFavoriteToggle(id);
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  return (
    <div className="relative rounded-lg border border-gray bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="h-40 w-full">
        <Link to={`products/${id}`}>
          <img className="mx-auto h-full" src={image} alt={name} />
        </Link>
      </div>

      <button
        onClick={handleToggleFavorite}
        className={`absolute top-3 right-3 text-xl ${
          isFavorite ? "text-red-600" : "text-red-400"
        } hover:text-red-600`}
      >
        <FaHeart />
      </button>

      <Link
        to={`/products/${id}`}
        className="mt-4 block text-lg font-semibold leading-tight text-gray-900 hover:underline dark:text-white"
      >
        {name}
      </Link>

      <div className="mt-2 flex flex-row items-center gap-2">
        <div className="flex items-center">
          {Array.from({ length: 5 }).map((_, i) => (
            <svg
              key={i}
              className={`h-4 w-4 ${
                i < rating ? "text-yellow-400" : "text-gray-300"
              }`}
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M13.8 4.2a2 2 0 0 0-3.6 0L8.4 8.4l-4.6.3a2 2 0 0 0-1.1 3.5l3.5 3-1 4.4c-.5 1.7 1.4 3 2.9 2.1l3.9-2.3 3.9 2.3c1.5 1 3.4-.4 3-2.1l-1-4.4 3.4-3a2 2 0 0 0-1.1-3.5l-4.6-.3-1.8-4.2Z" />
            </svg>
          ))}
        </div>
        <p className="text-xs font-medium text-gray-900 dark:text-white">
          {rating}
        </p>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
          ({reviews})
        </p>
      </div>

      <div className="mt-4 flex flex-col items-start gap-4">
        <p className="flex items-center text-2xl font-bold leading-tight text-gray-900 dark:text-white">
          <FaDollarSign /> {price}
        </p>
        <button
          type="button"
          onClick={handleAddToCart}
          className="inline-flex items-center rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-red-800 focus:outline-none focus:ring-4 focus:ring-red-300 dark:bg-red-700 dark:hover:bg-red-800 dark:focus:ring-red-900"
        >
          Add to cart
        </button>
      </div>
    </div>
  );
};

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { user: currentUser } = useAuth();
  const [favorites, setFavorites] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getProducts();
        setProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const toggleFavorite = (id: string) => {
    setFavorites((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-700 dark:text-gray-300">Loading products...</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {products.map((product) => (
        <ProductCard
          key={product.id}
          {...product}
          onFavoriteToggle={toggleFavorite}
          currentUser={currentUser}
          isFavorite={favorites[product.id] || false}
        />
      ))}
    </div>
  );
};

export default ProductList;
